import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';
import { computeTextColor, theme } from '@vimily/bonjoro-ui-components-v2';
import styled from '@emotion/styled';

export interface UploadFileItemProps {
  title: string;
  note?: string;
  fileName?: string;
  isLoading: boolean;
  onFileChange?: (fileType: string, fileSelected: File) => void;
  onFileDelete?: () => void;
  disableFileUpload?: boolean;
  disableDelete?: boolean;
  ctaCornerRadius?: number;
  ctaColor?: string;
  fontColor?: string;
  fontFamily?: string;
}

const UploadButtonIcon = styled.div<{color?: string;}>`
  background: ${({color}) => `${color} !important`};
`;

const UploadFileItem = React.forwardRef(
  (
    {
      title,
      note,
      fileName,
      isLoading,
      onFileChange,
      onFileDelete,
      disableFileUpload = false,
      disableDelete = false,
      ctaCornerRadius,
      ctaColor,
      fontColor,
      fontFamily,
    }: UploadFileItemProps,
    ref
  ) => {
    const canUploadFile = !disableFileUpload && !isLoading;

    const handleOnClick = () => {
      if (canUploadFile) {
        (ref as React.MutableRefObject<HTMLInputElement>)?.current?.click();
      }
    };

    const handleFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
      const fileSelected = evt.currentTarget.files?.[0];
      if (!fileSelected) return;

      const fileType = fileSelected.type;

      onFileChange?.(fileType, fileSelected);
    };

    const handleFileDelete = () => {
      if (canUploadFile) {
        onFileDelete?.();
      }
    };

    const { t } = useTranslation();

    const buttonTextColor = computeTextColor(ctaColor);

    const buttonProps = {
      variant: ButtonVariant.Secondary,
      className: 'w-full !h-10 flex justify-center items-center',
      onClick: handleOnClick,
      style: {
        borderRadius: `${ctaCornerRadius}px`,
        borderColor: ctaColor?.toLowerCase() !== theme.colors.white.toLowerCase() ? ctaColor : theme.colors.black,
        backgroundColor: ctaColor,
        color: buttonTextColor,
        fontFamily
      }
    };

    return (
      <div className='flex flex-col items-start mb-5'>
        <span className='pb-3 font-bold text-sm' style={{color: fontColor}}>{title}</span>
        <span className='pb-3 text-sm' style={{color: fontColor}}>{note}</span>
        <div className='w-full'>
          <Input
            inputRef={ref as React.MutableRefObject<HTMLInputElement>}
            onChange={handleFileChange}
            type='file'
            accept='image/png, image/jpeg, image/svg+xml'
            className='hidden absolute'
          />
          {isEmpty(fileName) ? (
            <Button {...buttonProps}>
              <UploadButtonIcon className='mr-3 icon icon-upload' color={buttonTextColor}/>
              <span className="text-sm">{t('testimonial:additionalInfo.form.addImage')}</span>
            </Button>
          ) : (
            <div
              className={cx(
                'flex w-full justify-between bg-white rounded items-center min-h-[38px]',
                'text-sm v2-text-neutral-900'
              )}
            >
              <div className='w-full text-left p-2 cursor-pointer truncate'>
                <span>
                  <i className='far fa-image mx-2' />
                  {isLoading ? (
                    <span className='v2-text-neutral-900'>
                      {t('testimonial:additionalInfo.form.loading')}
                    </span>
                  ) : (
                    fileName
                  )}
                </span>
              </div>
              <i
                className={cx('px-2', {
                  'far fa-trash': !isLoading,
                  'fa-spinner fa-spin': isLoading,
                  'cursor-pointer': !disableDelete,
                  'cursor-not-allowed text-neutral-400 pointer-events-none':
                    disableDelete,
                })}
                onClick={handleFileDelete}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default UploadFileItem;
